import { useCallback, useEffect, useState, useRef } from "react"

import MasterProductHeader from "./components/MasterProductHeader"
import { useNavigate } from "react-router"
import { useSapProductStore } from "../../stores"
import MasterProductCard from "./components/MasterProductCard"
import { ISapProduct } from "../../types/sapProduct.type"
import { useUserStore } from "../../stores/user/userStore"
import { TempLayout } from "../../layouts"
import { IGetEmployeeByIdParams } from "../../types"
import TitleSection from "../../components/common/TitleSection"
import SapApprovalStatusFilter from "@/features/shared/components/SapApprovalStatusFilter"

const SapMasterProducts = () => {
  const navigate = useNavigate()
  const { userData } = useUserStore()
  const { data, meta, isLoading, fetchProducts, reset } = useSapProductStore()
  const [allProducts, setAllProducts] = useState<ISapProduct[]>([])
  const prevDataLengthRef = useRef(0)
  const [params, setParams] = useState<IGetEmployeeByIdParams>({
    page: 1,
    per_page: 4,
    employee_id: '',
  })

  const handleRefetch = useCallback(async () => {
    if (params.employee_id) {
      await fetchProducts(params)
    }
  }, [fetchProducts, params])

  const handleReset = () => {
    reset()
  }

  const handleAddProduct = () => {
    navigate("/sapprd/create")
  }

  const handleApprovalStatusChange = (approvalStatus?: string) => {
    setAllProducts([])
    prevDataLengthRef.current = 0
    setParams(prev => ({
      ...prev,
      page: 1,
      approval_status: approvalStatus || undefined,
    }))
  }

  useEffect(() => {
    if (userData.employee_id) {
      setParams(prev => ({
        ...prev,
        employee_id: userData.employee_id,
      }))
    }
  }, [userData.employee_id])

  useEffect(() => {
    if (params.employee_id) {
      fetchProducts(params)
    }
  }, [fetchProducts, params, userData.employee_id])

  useEffect(() => {
    if (params.page === 1) {
      setAllProducts(data)
      prevDataLengthRef.current = data.length
    } else if (data.length !== prevDataLengthRef.current) {
      setAllProducts(prev => {
        const existingIds = new Set(prev.map(p => p.record_id))
        const newProducts = data.filter(p => !existingIds.has(p.record_id))
        return [...prev, ...newProducts]
      })
      prevDataLengthRef.current = data.length
    }
  }, [data, params.page])

  const handleLoadMore = () => {
    if (meta && params.page < meta.total_pages) {
      setParams(prev => ({
        ...prev,
        page: prev.page + 1,
      }))
    }
  }

  return (
    <TempLayout title="SAP - Products" navNavigateUrl="/home">
      {/* Header */}
      <TitleSection title="SAP - Products" onAdd={handleAddProduct} onRefresh={handleRefetch} />
      <SapApprovalStatusFilter value={params.approval_status} onChange={handleApprovalStatusChange} />

      <MasterProductCard
        products={allProducts}
        isLoading={isLoading || !meta}
        handleReset={() => {
          setAllProducts([])
          prevDataLengthRef.current = 0
          setParams(prev => ({ ...prev, page: 1, approval_status: undefined }))
          handleReset()
        }}
        onLoadMore={handleLoadMore}
        hasMore={meta ? params.page < meta.total_pages : true}
      />
    </TempLayout>
  )
}

export default SapMasterProducts
